import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Button from '../base/Button';

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showExpertModal, setShowExpertModal] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const scrollToSection = (sectionId: string) => {
    // If not on home page, navigate to home first then scroll
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        if (section) {
          section.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 300);
      return;
    }
    // Already on home page, just scroll
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const navigateToContact = () => {
    navigate('/contact');
    setShowExpertModal(false);
  };

  const handleEmailClick = () => {
    window.location.href = 'mailto:info@djtevpowertech.com';
  };

  const handlePhoneClick = () => {
    window.location.href = 'tel:18005700229';
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewsletterSubmitting(true);
    setNewsletterStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('email', newsletterEmail);

      const response = await fetch('https://readdy.ai/api/form/d9snmv94ne17ei0sdkl0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData
      });

      if (response.ok) {
        setNewsletterStatus('success');
        setNewsletterEmail('');
      } else {
        setNewsletterStatus('error');
      }
    } catch {
      setNewsletterStatus('error');
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  const handleServiceClick = (serviceType: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const event = new CustomEvent('openServiceModal', {
          detail: { serviceType }
        });
        window.dispatchEvent(event);
      }, 400);
      return;
    }
    scrollToSection('services');
    setTimeout(() => {
      const event = new CustomEvent('openServiceModal', {
        detail: { serviceType }
      });
      window.dispatchEvent(event);
    }, 1000);
  };

  const handleContactExpert = () => {
    setShowExpertModal(true);
  };

  const handlePhoneCall = () => {
    window.location.href = 'tel:18005700229';
    setShowExpertModal(false);
  };

  const handleEmailExpert = () => {
    window.location.href = 'mailto:info@djtevpowertech.com?subject=Expert Consultation Request&body=Hello DJT HAIKA team,%0D%0A%0D%0AI need expert consultation for EV charging solutions.%0D%0A%0D%0APlease contact me at your earliest convenience.%0D%0A%0D%0AThank you!';
    setShowExpertModal(false);
  };

  const handleScheduleCall = () => {
    navigate('/contact');
    setShowExpertModal(false);
  };

  return (
    <>
      <footer className="relative bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-white overflow-hidden">
        {/* Animated Star Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          {[...Array(30)].map((_, i) => {
            const size = (i % 3) + 1;
            const left = (i * 37 + 13) % 100;
            const delay = (i * 0.7) % 5;
            const duration = 2.5 + (i % 3) * 1.5;
            const opacity = 0.15 + (i % 4) * 0.15;
            const driftX = ((i % 5) - 2) * 12;
            const driftY = -20 - (i % 4) * 15;
            return (
              <div
                key={i}
                className="star-particle absolute rounded-full"
                style={{
                  width: `${size}px`,
                  height: `${size}px`,
                  left: `${left}%`,
                  top: `${10 + (i * 23) % 80}%`,
                  background: i % 5 === 0
                    ? 'rgba(254, 240, 138, 1)'
                    : i % 3 === 0
                      ? 'rgba(253, 224, 71, 0.8)'
                      : 'rgba(255, 255, 255, 0.6)',
                  boxShadow: i % 5 === 0
                    ? '0 0 6px 2px rgba(254, 240, 138, 0.7), 0 0 12px 4px rgba(254, 240, 138, 0.3)'
                    : i % 3 === 0
                      ? '0 0 4px 1px rgba(253, 224, 71, 0.5)'
                      : '0 0 3px 1px rgba(255, 255, 255, 0.3)',
                  opacity: opacity,
                  animation: `star-float-${i % 3} ${duration}s ease-in-out ${delay}s infinite`,
                  '--drift-x': `${driftX}px`,
                  '--drift-y': `${driftY}px`,
                } as React.CSSProperties}
              />
            );
          })}
        </div>

        {/* Newsletter Section */}
        <div className="relative z-10 border-b border-white/10">
          <div className="container mx-auto px-4 py-14">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center space-x-2 bg-white/5 backdrop-blur-sm rounded-full px-4 py-1.5 border border-white/10 mb-6">
                <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></div>
                <span className="text-amber-300 text-xs font-medium tracking-wide">STAY CONNECTED</span>
              </div>
              <h3 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-white via-amber-200 to-white bg-clip-text text-transparent">
                Power Up Your EV Knowledge
              </h3>
              <p className="text-gray-400 mb-8 text-lg leading-relaxed max-w-xl mx-auto">
                Get the latest EV charging insights, government policy updates, and exclusive offers delivered straight to your inbox.
              </p>
                <form data-readdy-form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                  <input
                    type="email"
                    name="email"
                    required
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="flex-1 px-5 py-3.5 bg-white/10 border border-white/20 rounded-full text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-400 transition-all duration-300"
                  />
                  <button
                    type="submit"
                    disabled={newsletterSubmitting}
                    className="px-8 py-3.5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white font-bold rounded-full transition-all duration-300 shadow-lg shadow-amber-500/20 disabled:opacity-50 whitespace-nowrap text-sm"
                  >
                    {newsletterSubmitting ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </form>
                {newsletterStatus === 'success' && (
                  <p className="mt-4 text-emerald-400 text-sm font-medium">You are now subscribed! Check your inbox for confirmation.</p>
                )}
                {newsletterStatus === 'error' && (
                  <p className="mt-4 text-red-400 text-sm font-medium">Something went wrong. Please try again.</p>
                )}
            </div>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="relative z-10 container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">
            {/* Company Info - Takes 2 cols */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-5">
                <img
                  src="https://static.readdy.ai/image/b8a021112837a070edfa44e380da93b7/1465110942c76789698a862651f928ae.png"
                  alt="DJT EV POWER TECH PVT LTD Logo"
                  className="w-12 h-12 object-contain rounded-lg"
                />
                <div>
                  <h3 className="text-lg font-bold text-white leading-tight">DJT EV POWER TECH</h3>
                  <h3 className="text-lg font-bold text-white leading-tight">PVT LTD</h3>
                </div>
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed text-sm max-w-md">
                India's premier AI-powered EV charging infrastructure provider. Delivering intelligent, sustainable, and efficient charging solutions from 3.3kW to 240kW for all electric vehicles — driving the nation toward a greener tomorrow.
              </p>

              {/* Quick Contact Pills */}
              <div className="flex flex-wrap gap-3 mb-6">
                <button
                  onClick={handlePhoneClick}
                  className="inline-flex items-center space-x-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-amber-500/30 rounded-full px-4 py-2 transition-all duration-300 group cursor-pointer"
                >
                  <i className="ri-phone-line text-amber-400 group-hover:text-amber-300 text-sm"></i>
                  <span className="text-gray-300 group-hover:text-white text-sm">1-800-570-0229</span>
                </button>
                <button
                  onClick={handleEmailClick}
                  className="inline-flex items-center space-x-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-amber-500/30 rounded-full px-4 py-2 transition-all duration-300 group cursor-pointer"
                >
                  <i className="ri-mail-line text-amber-400 group-hover:text-amber-300 text-sm"></i>
                  <span className="text-gray-300 group-hover:text-white text-sm">info@djtevpowertech.com</span>
                </button>
              </div>

              {/* Social Icons */}
              <div className="flex items-center space-x-2">
                {[
                  { icon: 'ri-facebook-fill', label: 'Facebook', url: 'https://facebook.com/djthaika' },
                  { icon: 'ri-twitter-fill', label: 'Twitter', url: 'https://twitter.com/djthaika' },
                  { icon: 'ri-linkedin-fill', label: 'LinkedIn', url: 'https://linkedin.com/company/djthaika' },
                  { icon: 'ri-instagram-fill', label: 'Instagram', url: 'https://instagram.com/djthaika' },
                  { icon: 'ri-youtube-fill', label: 'YouTube', url: 'https://youtube.com/@djthaika' },
                ].map((social, idx) => (
                  <a
                    key={idx}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-9 h-9 rounded-full bg-white/5 hover:bg-amber-500/20 border border-white/10 hover:border-amber-500/30 flex items-center justify-center transition-all duration-300 group cursor-pointer"
                  >
                    <i className={`${social.icon} text-gray-400 group-hover:text-amber-400 text-sm transition-colors duration-300`}></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-bold mb-5 text-sm uppercase tracking-wider flex items-center">
                <span className="w-6 h-0.5 bg-amber-500 mr-2 rounded-full"></span>
                Explore
              </h4>
              <ul className="space-y-3">
                {[
                  { label: 'Home', id: 'home' },
                  { label: 'About Us', id: 'about' },
                  { label: 'Our Products', id: 'products' },
                  { label: 'Government Orders', id: 'government-orders' },
                  { label: 'EV Subsidies', id: 'subsidies' },
                  { label: 'Testimonials', id: 'testimonials' },
                ].map((link) => (
                  <li key={link.id}>
                    <button
                      onClick={() => scrollToSection(link.id)}
                      className="text-gray-400 hover:text-amber-400 transition-all duration-300 cursor-pointer text-sm flex items-center group"
                    >
                      <i className="ri-arrow-right-s-line text-transparent group-hover:text-amber-400 -ml-1 mr-1 transition-all duration-300"></i>
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-white font-bold mb-5 text-sm uppercase tracking-wider flex items-center">
                <span className="w-6 h-0.5 bg-amber-500 mr-2 rounded-full"></span>
                Services
              </h4>
              <ul className="space-y-3">
                {[
                  { label: 'DC Fast Charging', id: 'dc-fast-charging', icon: 'ri-flashlight-line' },
                  { label: 'AC Charging', id: 'ac-charging', icon: 'ri-home-line' },
                  { label: 'Installation & Setup', id: 'installation', icon: 'ri-tools-line' },
                  { label: 'Maintenance Support', id: 'maintenance', icon: 'ri-settings-line' },
                  { label: 'Consulting', id: 'consulting', icon: 'ri-chat-3-line' },
                  { label: 'Smart Management', id: 'smart-management', icon: 'ri-smartphone-line' },
                ].map((service) => (
                  <li key={service.id}>
                    <button
                      onClick={() => handleServiceClick(service.id)}
                      className="text-gray-400 hover:text-amber-400 transition-all duration-300 cursor-pointer text-sm flex items-center group w-full text-left"
                    >
                      <i className={`${service.icon} mr-2 text-gray-500 group-hover:text-amber-400 transition-colors duration-300 text-xs`}></i>
                      {service.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact & Policies */}
            <div>
              <h4 className="text-white font-bold mb-5 text-sm uppercase tracking-wider flex items-center">
                <span className="w-6 h-0.5 bg-amber-500 mr-2 rounded-full"></span>
                Support
              </h4>

              {/* Contact Info */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center space-x-3 text-gray-400 text-sm">
                  <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-amber-400"></i>
                  </div>
                  <div>
                    <p className="text-white text-xs font-medium">24/7 Support</p>
                    <p className="text-gray-500 text-xs">Always available</p>
                  </div>
                </div>
                <a href="mailto:support@djtevpowertech.com" className="flex items-center space-x-3 text-gray-400 text-sm hover:text-amber-400 transition-colors duration-300 group">
                  <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:border-amber-500/30">
                    <i className="ri-mail-line text-amber-400"></i>
                  </div>
                  <div>
                    <p className="text-white text-xs font-medium">Email Us</p>
                    <p className="text-gray-500 text-xs">support@djtevpowertech.com</p>
                  </div>
                </a>
                <div className="flex items-center space-x-3 text-gray-400 text-sm">
                  <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-amber-400"></i>
                  </div>
                  <div>
                    <p className="text-white text-xs font-medium">Headquarters</p>
                    <p className="text-gray-500 text-xs">Visakhapatnam, AP, India</p>
                  </div>
                </div>

                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 text-amber-400 hover:text-amber-300 transition-colors duration-300 text-sm mt-1 group"
                >
                  <span>Full Contact Page</span>
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
                </Link>
              </div>

              {/* Policy Links */}
              <div className="space-y-2.5 pt-4 border-t border-white/10">
                <h5 className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-3">Policies</h5>
                <Link
                  to="/return-policy"
                  className="flex items-center text-gray-400 hover:text-amber-400 transition-all duration-300 text-sm group"
                >
                  <i className="ri-refund-2-line mr-2 text-xs group-hover:text-amber-400 transition-colors duration-300"></i>
                  Return & Refund Policy
                </Link>
                <Link
                  to="/payment-policy"
                  className="flex items-center text-gray-400 hover:text-amber-400 transition-all duration-300 text-sm group"
                >
                  <i className="ri-secure-payment-line mr-2 text-xs group-hover:text-amber-400 transition-colors duration-300"></i>
                  Payment Policy
                </Link>
                <Link
                  to="/privacy-policy"
                  className="flex items-center text-gray-400 hover:text-amber-400 transition-all duration-300 text-sm group"
                >
                  <i className="ri-shield-check-line mr-2 text-xs group-hover:text-amber-400 transition-colors duration-300"></i>
                  Privacy Policy
                </Link>
                <Link
                  to="/delete-account"
                  className="flex items-center text-gray-400 hover:text-amber-400 transition-all duration-300 text-sm group"
                >
                  <i className="ri-delete-bin-6-line mr-2 text-xs group-hover:text-amber-400 transition-colors duration-300"></i>
                  Delete Account
                </Link>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center space-x-2 text-gray-500 text-xs">
              <i className="ri-copyright-line"></i>
              <span>2024 DJT EV POWER TECH PVT LTD. All rights reserved.</span>
            </div>

            <div className="flex items-center space-x-6">
              <Link to="/return-policy" className="text-gray-500 hover:text-amber-400 text-xs transition-colors duration-300">
                Returns
              </Link>
              <Link to="/payment-policy" className="text-gray-500 hover:text-amber-400 text-xs transition-colors duration-300">
                Payments
              </Link>
              <Link to="/privacy-policy" className="text-gray-500 hover:text-amber-400 text-xs transition-colors duration-300">
                Privacy
              </Link>
              <Link to="/delete-account" className="text-gray-500 hover:text-amber-400 text-xs transition-colors duration-300">
                Delete Account
              </Link>
              <Link to="/contact" className="text-gray-500 hover:text-amber-400 text-xs transition-colors duration-300">
                Contact
              </Link>
              <span className="text-gray-500 hover:text-amber-400 text-xs transition-colors duration-300">
                Powered by DJT EV POWER TECH PVT LTD
              </span>
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Expert Modal */}
      {showExpertModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full mx-4 p-6 md:p-8 relative animate-scale-in max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowExpertModal(false)}
              className="absolute top-4 right-4 w-10 h-10 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 shadow-lg z-10 hover:shadow-xl transform hover:scale-105"
            >
              <i className="ri-close-line text-lg"></i>
            </button>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 relative">
                <i className="ri-customer-service-2-line text-2xl text-white"></i>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full animate-pulse border-2 border-white"></div>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Connect with Our EV Expert</h3>
              <p className="text-gray-600 mb-6 text-base md:text-lg">Get instant expert guidance for your EV charging needs. Our specialists are available 24/7 to help you make the right choice.</p>

              <div className="space-y-4">
                <button
                  className="w-full justify-center bg-amber-500 hover:bg-amber-600 py-4 px-4 rounded-xl text-white font-semibold transition-all duration-300 flex items-center cursor-pointer text-base"
                  onClick={handlePhoneCall}
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call Now
                  <span className="ml-2 text-amber-200">1-800-570-0229</span>
                </button>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    className="w-full justify-center bg-purple-600 hover:bg-purple-700 py-3 px-4 rounded-xl text-white font-semibold transition-all duration-300 flex items-center cursor-pointer text-sm"
                    onClick={handleEmailExpert}
                  >
                    <i className="ri-mail-line mr-2"></i>
                    Email Expert
                  </button>
                  <button
                    className="w-full justify-center bg-cyan-600 hover:bg-cyan-700 py-3 px-4 rounded-xl text-white font-semibold transition-all duration-300 flex items-center cursor-pointer text-sm"
                    onClick={() => window.open('https://wa.me/919154673422?text=Hi, I need EV charging consultation', '_blank')}
                  >
                    <i className="ri-whatsapp-line mr-2"></i>
                    WhatsApp
                  </button>
                </div>

                <button
                  className="w-full justify-center bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 py-3 px-4 rounded-xl text-white font-semibold transition-all duration-300 flex items-center cursor-pointer text-sm"
                  onClick={handleScheduleCall}
                >
                  <i className="ri-calendar-line mr-2"></i>
                  Schedule Site Visit & Consultation
                </button>
              </div>

              <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div className="flex items-center justify-center mb-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></div>
                  <p className="text-sm font-bold text-amber-800">Expert Available Now</p>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center text-xs">
                  <div>
                    <p className="font-medium text-amber-800">Response Time</p>
                    <p className="text-amber-600">Under 2 min</p>
                  </div>
                  <div>
                    <p className="font-medium text-amber-800">Consultation</p>
                    <p className="text-amber-600">100% Free</p>
                  </div>
                  <div>
                    <p className="font-medium text-amber-800">Support</p>
                    <p className="text-amber-600">24/7</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }

        /* Star Float Animations */
        @keyframes star-float-0 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.3; }
          25% { transform: translate(var(--drift-x), var(--drift-y)) scale(1.4); opacity: 0.8; }
          50% { transform: translate(calc(var(--drift-x) * -0.5), calc(var(--drift-y) * -0.3)) scale(0.8); opacity: 0.2; }
          75% { transform: translate(calc(var(--drift-x) * 0.7), calc(var(--drift-y) * 0.5)) scale(1.3); opacity: 0.7; }
        }
        @keyframes star-float-1 {
          0%, 100% { transform: translate(0, 0) scale(0.8); opacity: 0.4; }
          33% { transform: translate(calc(var(--drift-x) * -1), var(--drift-y)) scale(1.5); opacity: 0.9; }
          66% { transform: translate(calc(var(--drift-x) * 0.5), calc(var(--drift-y) * -0.6)) scale(0.7); opacity: 0.15; }
        }
        @keyframes star-float-2 {
          0%, 100% { transform: translate(0, 0) scale(1.1); opacity: 0.5; }
          20% { transform: translate(var(--drift-x), calc(var(--drift-y) * -0.5)) scale(0.6); opacity: 0.1; }
          50% { transform: translate(calc(var(--drift-x) * 0.3), var(--drift-y)) scale(1.6); opacity: 0.85; }
          80% { transform: translate(calc(var(--drift-x) * -0.8), calc(var(--drift-y) * 0.4)) scale(0.9); opacity: 0.35; }
        }

        .star-particle {
          will-change: transform, opacity;
        }
      `}</style>
    </>
  );
}